import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import axios from "axios";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { ReactComponent as AvatarSvg } from "../../../static/assets/avatar.svg";
import { ReactComponent as NotificationSvg } from "../../../static/assets/Notification.svg";

function getActiveNotifications(user, container) {
  const list = [];
  const hasCollectorEmail = Boolean(
    container?.collectorEmail ?? container?.collectorEmailJoin,
  );
  const alertForFilling = Boolean(container?.alertForFilling);
  const alertForAnomaly = Boolean(container?.alertForAnomaly);
  const characterization = Boolean(container?.characterization);

  if (
    alertForAnomaly &&
    (user?.receiveAnomalyMail ||
      user?.receiveAnomalyPush ||
      user?.receiveAnomalySms)
  ) {
    list.push({
      id: "anomaly",
      labelKey: "containerSettings.alertSortingError",
    });
  }

  if (alertForFilling) {
    const userWantsRotation =
      user?.receiveRotationMail ||
      user?.receiveRotationPush ||
      user?.receiveRotationSms;
    const userWantsFilling =
      user?.receiveFillingMail ||
      user?.receiveFillingPush ||
      user?.receiveFillingSms;
    if (hasCollectorEmail && userWantsRotation) {
      list.push({
        id: "rotation",
        labelKey: "containerSettings.alertRotation",
      });
    } else if (userWantsFilling) {
      list.push({
        id: "filling",
        labelKey: "containerSettings.alertFillingThreshold",
      });
    }
  }

  if (
    characterization &&
    (user?.recieveCharacterizationMail ||
      user?.recieveCharacterizationPush ||
      user?.recieveCharacterizationSms)
  ) {
    list.push({
      id: "characterization",
      labelKey: "containerSettings.characterization",
    });
  }
  return list;
}

/**
 * Onglet "Gestionnaires" – mêmes gestionnaires que "Gestionnaires de ce conteneur"
 * (Caractéristiques). Utilisateurs dont authorizedAddresses contient l’adresse du conteneur.
 */
const ZoneSettingsManagers = ({ container }) => {
  const { t } = useTranslation();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const addressId = container?.addressId ?? container?.addressJoin?._id;
  const hasAttachmentSite =
    addressId != null &&
    addressId !== "" &&
    addressId !== "unknown" &&
    String(addressId).length === 24;

  useEffect(() => {
    if (!hasAttachmentSite) {
      setManagers([]);
      setLoading(false);
      return;
    }
    const token = Cookies.get("userToken");
    const API = process.env.REACT_APP_API_BACKEND;
    const fetchManagers = async () => {
      try {
        const response = await axios.get(`${API}users?page=1&limit=100`, {
          headers: { authorization: "Bearer " + token },
          params: {
            keywordFilter: "",
            sortByField: "firstName",
            orderSortByField: "1",
            listRoleFilter: {},
          },
        });
        if (response?.status === 200 && Array.isArray(response.data?.data)) {
          const addressIdStr =
            typeof addressId === "string" ? addressId : addressId?.toString?.();
          const filtered = response.data.data.filter((user) => {
            if ((user?.email || "").includes("@akanthas.")) return false;
            const addrs = user?.authorizedAddresses || [];
            return addrs.some(
              (id) =>
                (typeof id === "string" ? id : id?.toString?.()) ===
                addressIdStr,
            );
          });

          setManagers(filtered);
        } else {
          setManagers([]);
        }
      } catch {
        setManagers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchManagers();
  }, [addressId, hasAttachmentSite]);

  return (
    <div className="zone-settings__managers">
      <div className="zone-settings__managers-wrap">
        <h3 className="zone-settings__managers-title">
          {t("containerSettings.siteManagers")}
        </h3>

        {!hasAttachmentSite ? (
          <p className="zone-settings__managers-empty">
            {t("settingsContainers.selectSiteForManagers")}
          </p>
        ) : loading ? (
          <InlineLoader
            height="120px"
            className="zone-settings__managers-loading"
          />
        ) : managers.length === 0 ? (
          <p className="zone-settings__managers-empty">
            {t("containerSettings.noManagersForSite")}
          </p>
        ) : (
          <div className="zone-settings__managers-list">
            {managers.map((user) => {
              const notifications = getActiveNotifications(user, container);
              const isExpanded = expandedId === user._id;
              return (
                <div key={user._id} className="zone-settings__managers-card">
                  <div className="zone-settings__managers-card-header">
                    <div className="zone-settings__managers-avatar" aria-hidden>
                      <AvatarSvg className="zone-settings__managers-avatar-icon" />
                    </div>
                    <div className="zone-settings__managers-card-info">
                      <div className="zone-settings__managers-name">
                        {[user.firstName, user.lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </div>
                      <div className="zone-settings__managers-job">
                        <span className="zone-settings__managers-job-label">
                          {user.role
                            ? capitalizeFirstLetter(t(user.role))
                            : t("containerSettings.jobTitlePlaceholder")}
                        </span>
                      </div>
                      <div className="zone-settings__managers-notifications mt-2">
                        <button
                          type="button"
                          className="zone-settings__managers-notifications-toggle"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : user._id)
                          }
                          aria-expanded={isExpanded}
                        >
                          <span className="zone-settings__managers-notifications-line">
                            <NotificationSvg className="zone-settings__managers-notifications-icon" />
                            <span className="zone-settings__managers-notifications-summary">
                              {t("containerSettings.notificationsActivated")} (
                              {notifications.length})
                            </span>
                          </span>
                          <i
                            className={`fas fa-chevron-down zone-settings__managers-notifications-chevron ${isExpanded ? "is-open" : ""}`}
                            aria-hidden
                          />
                        </button>
                        {isExpanded && (
                          <ul className="zone-settings__managers-notifications-list">
                            {notifications.length === 0 ? (
                              <li className="zone-settings__managers-notifications-empty">
                                {t("containerSettings.noNotificationActivated")}
                              </li>
                            ) : (
                              notifications.map((n) => (
                                <li key={n.id}>{t(n.labelKey)}</li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneSettingsManagers;
